<template>
    <div>
        <div :class="`${scopeId}`">
            <component :is="`protokol-power-app-template-${app.uuid}`" v-on="$listeners" v-bind="$attrs"
                :config="app.config" />
        </div>

        <b-alert :show="errorModal.show" variant="danger">
            {{ errorModal.msg }}
        </b-alert>
    </div>
</template>

<script>

import { 
    Thunder,
    Component,
    Project,
    Integrations,
    Ratchet,
    Sandbox,
} from '@protokol/platform-libs'
import printJS from 'print-js'
import axios from 'axios'
import moment from 'moment'
import Vue from "vue"
import { injectGlobal } from 'vue-styled-components'
import VueRouter from 'vue-router'
import * as CoreUI from '@/components/ui/vue/src'
import Compiler from '@/components/renderer'
import { Howl } from 'howler'

// adapters
import PlatformAdapter from '@/lib/adapters/platform'

import TemplateComponents from './templates'

export default {
    name: 'PowerAppV3',
    components: {},
    props: {
        app: {
            type: Object
        },
        entrypoint: {
            type: String,
            default: "index"
        },
        dist: {
            type: Object
        },
        noRouter: {
            type: Boolean,
            default: false
        }
    },

    data: function () {
        return {
            context: {
                vars: {}
            },
            errorModal: {
                show: false,
                msg: null
            },
            modules: {},
            exports: {},
            components: {},
            services: {},
            packageImports: {}
        }
    },

    methods: {
        showErrorModal(msg) {
            this.errorModal.show = true;
            this.errorModal.msg = msg;
        },

        find(collection, f) {
            return this.$store.getters.coldStorageFind(collection, f)
        },

        import(path) {
            return this.require(path)
        },

        require(path, options) {
            // get extension
            let parts = path.split(".")
            let ext = parts[parts.length - 1]
            let node = this.dist[path] || null
            if (!node) {
                if (!options.ignorePathError) {
                    this.showErrorModal("Can't resolve path (" + path + ") to import module.")
                }

                return;
            }

            if (ext == 'js') {
                try {
                    let m = (new Compiler()).evaluateJS(node, this.libs()).default
                    if (m && m.stack && m.message) {
                        throw new Error(m.message)
                    }

                    return m
                } catch (err) {
                    console.error(err)
                    throw new Error(err.message)
                }

            }

            if (ext == 'vue') {
                return () => ({
                    component: this.register(node),
                })
            }

            if (ext == 'css') {
                this.parseStyles(node)
            }

            if (ext == 'svg') {
                return node
            }

            if (ext == 'json') {
                return JSON.parse(node)
            }
        },

        async register(node) {
            try {
                let compiler = new Compiler();
                let result = compiler.evaluateJS(node.script, this.libs()).default

                if (result && result.stack && result.message) {
                    throw new Error(result.message)
                }

                result.template = node.template

                result.components = {
                    ...result.components,
                    ...CoreUI,
                    ...this.components,
                    ...TemplateComponents,
                }

                // scope styles only to the component
                this.parseStyles(node.style)

                return result
            } catch (err) {
                console.error(err)
                this.showErrorModal(err.message)
            }
        },

        async parseStyles(content) {
            injectGlobal`${content}`
        },

        displayError(message) {
            this.$bvToast.toast(message, {
                variant: 'danger',
                title: "Error",
            })
        },

        goToEditor() {
            this.$emit('goToEditor')
        },

        libs() {
            return {
                session: this.$store.getters.getUser,
                resource: new Thunder(new PlatformAdapter(this), true),
                thunder: new Thunder(new PlatformAdapter(this), true),
                component: new Component(new PlatformAdapter(this), `platform-app::${this.app.name}`),
                integrations: new Integrations(new PlatformAdapter(this)),
                ratchet: new Ratchet(new PlatformAdapter(this)),
                sandbox: new Sandbox(new PlatformAdapter(this)),
                project: new Project(new PlatformAdapter(this)),
                require: this.require,
                register: this.register,
                print: printJS,
                axios: axios,
                moment: moment,
                lodash: _,
                localStorage: undefined,
                sessionStorage: undefined,

                log: {
                    info: (...args) => {
                        console.log(...args)
                    },

                    error: (err) => {
                        console.error(err)
                    }
                },

                util: {
                    dateToUserTimezone: this.$toUserTimeZone,
                    dateToTimezone: (time, zone, format) => {
                        let defaultFormat = 'YYYY-MM-DD HH:mm:ss';

                        if (format) {
                            defaultFormat = format
                        }

                        if (zone === null || zone === undefined || zone == "") {
                            return moment(time).tz(moment.tz.guess()).format(defaultFormat);
                        }

                        return moment(time).tz(zone).format(defaultFormat);
                    },

                    dateToUserTimezoneAsTime: (time) => {
                        return moment(this.$toUserTimeZone(time))
                    },

                    dateToTimezoneAsTime: (time, zone) => {
                        if (zone === null || zone === undefined || zone == "") {
                            return moment(time).tz(moment.tz.guess());
                        }

                        return moment(time).tz(zone);
                    },
                },

                app: {
                    showLoadingPopup: (t) => this.$loadingPopup(t),
                    store: (collection, data) => { this.$store.commit("COLD_STORAGE_STORE", { collection: collection, data: data }) },
                    find: (collection, f) => { return this.$store.getters.coldStorageFind(collection, f) },
                    openWindow: (link, target) => {
                        window.open(link, target)
                    },
                    getEnv: (key, def) => {
                        let config = this.require('config.js', { ignorePathErrors: true })
                        let currentEnv = this.$store.getters.getCurrentEnv

                        return _.get(config.env, `${currentEnv}.${key}`, def)
                    },
                    notify: (type, text) => {
                        this.$bvToast.toast(text, {
                            variant: 'danger',
                            title: (type === 'success' ? 'Success' : (type === 'error' ? 'Error' : type)),
                        })
                    },

                    interval: {
                        set: (fn, tm) => {
                            return window.setInterval(function () {
                                fn()
                            }, tm)
                        },

                        clear: (interval) => {
                            window.clearInterval(interval)
                        }
                    },

                    audio: {
                        play: (file, options) => {
                            var sound = new Howl({
                                src: [file],
                                volume: 1,
                                ...options
                            });

                            sound.play();

                            return sound
                        },

                        playNotification: (options) => {
                            var sound = new Howl({
                                src: ['../sound/notification.wav'],
                                volume: 1,
                                ...options
                            });

                            sound.play();

                            return sound
                        }
                    },

                    registerComponent: async (current, name, template) => {
                        let compiler = new Compiler();
                        let r = await compiler.compileVue(name, template)
                        let t = await this.register(r)

                        Vue.component(name, t)
                    },

                    eval(expr, params) {
                        let keys = Object.keys(params)
                        let values = Object.values(params)

                        let result = new Function(...keys, `try{ ${expr} } catch (err) { return err; }`)

                        return result(...values)
                    },

                    storage: {
                        get: (key) => {
                            return localStorage.getItem(`${this.app.uuid}_${key}`)
                        },

                        set: (key, value) => {
                            return localStorage.setItem(`${this.app.uuid}_${key}`, value)
                        },

                        remove: (key) => {
                            return localStorage.removeItem(`${this.app.uuid}_${key}`)
                        }
                    }
                },

                user: {
                    roles: async () => {
                        return this.$store.dispatch('fetchRoles')
                    },

                    can: (action) => {
                        return this.$can(action)
                    }
                },

                event: {
                    listen(e, fn) {
                        window.addEventListener(e, (ev) => {
                            fn(ev)
                        })
                    }
                },

                loader: {
                    loadSVG: (path, name) => {
                        let content = this.require(path)
                        this.components[name] = Vue.extend(content)
                    }
                },

                file: {
                    readAsDataUrl: (file, options) => {
                        return new Promise((resolve, reject) => {
                            var reader = new FileReader()
                            reader.readAsDataURL(file, options)
                            reader.onload = () => resolve(reader.result);
                            reader.onerror = error => reject(error);
                        })
                    }
                },

                eventbus: this.$eventBus,

                voyager: {
                    async call(fname, ...args) {
                        if (!window.flutter_inappwebview) {
                            throw new Error("invalid platform");
                        }

                        return await window.flutter_inappwebview.callHandler(fname, ...args);
                    }
                },

                exports: {}
            }
        }
    },

    computed: {
        template() {
            try {
                return this.dist[this.entrypoint + ".vue"]
            } catch (err) {
                this.$debugLog(err)
                this.showErrorModal("Did you properly formated your index template?");
            }
        },

        scopeId() {
            return this.app.uuid.replace(/[0-9]/g, '').replaceAll('-', '');
        }

    },

    destroyed() {
        this.$eventBus.close()
    },

    created() {
        this.$eventBus.connect()

        let compiler = new Compiler();
        let result = compiler.evaluateJS(this.template.script, this.libs()).default || {}

        if (result && result.stack && result.message) {
            this.showErrorModal(result.message)
            return
        }

        try {
            this.parseStyles(this.template.style)

            result.components = {
                ...result.components,
                ...CoreUI,
                ...this.components,
                ...TemplateComponents,
            }

            let component = {
                ...result,
                template: this.template.template
            }

            if (!this.noRouter) {
                component.router = new VueRouter({
                    // 4. Provide the history implementation to use. We are using the hash history for simplicity here.
                    routes: this.require('routes.js', { ignorePathError: true }) || [], // short for `routes: routes`
                    base: this.$route.path
                })
            }

            Vue.component('protokol-power-app-template-' + this.app.uuid, component)
        } catch (err) {
            console.error(err)
        }
    },

    beforeDestroy() {
        // Get a reference to the last interval + 1
        const interval_id = window.setInterval(function () { }, Number.MAX_SAFE_INTEGER);

        // Clear any timeout/interval up to that id
        for (let i = 1; i < interval_id; i++) {
            window.clearInterval(i);
        }
    }
}
</script>